import axios from "axios";
import React from "react";
import { CookiesProvider } from "react-cookie";
import { toast, ToastContainer } from "react-toastify";

import { ApolloProvider } from "@apollo/client";
import { IntelligemsThemeProvider } from "@intelligems/ig-theme";
import { StyledEngineProvider } from "@mui/material";
import { Theme } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import "@mui/styles";

import { AppProvider } from "./providers/AppProvider";
import { shopifyGqlClient } from "./providers/gqlProvider";
import {
  OnQueryErrorAction,
  OnQuerySuccessAction,
  QueryMeta,
} from "./types/react-query";
import { AppRoutes } from "./routes";
import theme from "./theme";
import { Logout } from "./utils";

import "react-toastify/dist/ReactToastify.css";
import "./App.css";

declare module "@mui/styles/defaultTheme" {
  interface DefaultTheme extends Theme {}
}

const MS_IN_HOUR = 60 * 1000 * 60;
const cacheTime = MS_IN_HOUR * 24;

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error) => {
        if (axios.isAxiosError(error)) {
          toast.error(
            `Something went wrong... ${
              error?.response?.data?.error || error?.message
            }`
          );
          if (!error.response) Sentry.captureException(error);
        }
      },
    },
    queries: {
      retry: 0,
      staleTime: cacheTime,
    },
  },
  queryCache: new QueryCache({
    onSuccess: (data, query) => {
      if (!query.meta) {
        return;
      }

      const queryMeta = query.meta as QueryMeta;
      if (!queryMeta.onSuccess) {
        return;
      }

      const action = queryMeta.onSuccess as OnQuerySuccessAction | undefined;
      if (!action) {
        return;
      }

      if (typeof action === "string") {
        toast.success(action);
        return;
      }

      if (typeof action === "function") {
        action(data);
        return;
      }
    },
    onError: (error, query) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error === "token error") {
          Logout();
          return;
        }

        const queryMeta = query.meta as QueryMeta | undefined;
        if (!queryMeta || !queryMeta.onError || !error.response?.status) {
          toast.error(`Something went wrong... ${error.response?.data.error}`);
          return;
        }

        const action = (queryMeta.onError[error.response.status] ||
          queryMeta.onError["any"] ||
          undefined) as OnQueryErrorAction | undefined;

        if (action === undefined) {
          toast.error(`Something went wrong... ${error.response?.data.error}`);
          return;
        }

        if (action === "ignore" || action === "") {
          return;
        }

        if (typeof action === "function") {
          action(error);
          return;
        }

        toast.error(action);
      } else {
        // No response from server at all
        Sentry.captureException(error);
      }
    },
  }),
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={shopifyGqlClient}>
        <StyledEngineProvider injectFirst>
          {/*// @ts-ignore*/}
          <IntelligemsThemeProvider theme={theme}>
            <CookiesProvider>
              <ReactQueryDevtools initialIsOpen={false} />
              <ToastContainer autoClose={5000} />
              <AppProvider>
                <AppRoutes />
              </AppProvider>
            </CookiesProvider>
          </IntelligemsThemeProvider>
        </StyledEngineProvider>
      </ApolloProvider>
    </QueryClientProvider>
  );
};

export default Sentry.withProfiler(App);
